import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../auth/session';
import { SetupWizard } from './SetupWizard';

export type ChecklistItem = {
  key: string;
  label: string;
  done: boolean;
  optional: boolean;
};

export type OnboardingState = {
  tenant_id: number;
  current_step: number;
  max_step: number;
  checklist: ChecklistItem[];
  completed_at: string | null;
  dismissed: boolean;
  created_at: string;
  updated_at: string;
};

// Blueprint: 5 step totali. Step 1-2 in RegisterPage (fiscale + categoria).
// OnboardingBanner post-reg: step 3 (configurazione), 4 (moduli), 5 (primo contenuto)

export function OnboardingBanner() {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(true);

  const [migrationMsg, setMigrationMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/tenant/onboarding');
      if (!res.ok) return;
      const data = (await res.json()) as OnboardingState;
      setState(data);

      // G4: Check for recent category migration
      const migRes = await apiFetch('/api/v1/tenant/category/migrations');
      if (migRes.ok) {
        const migData = await migRes.json() as { migrations?: Array<{ phase: string; from_category_code: string; to_category_code: string; completed_at: string }> };
        const recent = migData.migrations?.[0];
        if (recent?.phase === 'completed' && recent.completed_at) {
          const hoursAgo = (Date.now() - new Date(recent.completed_at).getTime()) / 3600_000;
          if (hoursAgo < 24) {
            setMigrationMsg(`Categoria cambiata da ${recent.from_category_code} a ${recent.to_category_code} — configura il nuovo verticale`);
          }
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading || !state) return null;

  // G4: Show migration banner even if onboarding is completed/dismissed
  if (migrationMsg && (state.dismissed || state.completed_at)) {
    return (
      <div className="sk-saas-card" style={{
        padding: '0.85rem 1.25rem', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
        background: 'var(--sk-box-warning-bg)', border: '1px solid var(--sk-box-warning-border)',
      }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--sk-box-warning-fg)' }}>
          <strong>🔄 {migrationMsg}</strong>
        </div>
        <button className="sk-btn sk-btn--quiet" onClick={() => setMigrationMsg(null)}>✕</button>
      </div>
    );
  }

  if (state.dismissed || state.completed_at) return null;

  const requiredCount = state.checklist.filter((i) => !i.optional).length;
  const requiredDone = state.checklist.filter((i) => !i.optional && i.done).length;
  const progressPct = requiredCount > 0 ? Math.round((requiredDone / requiredCount) * 100) : 0;

  return (
    <>
      {/* Banner */}
      <div className="sk-saas-card" style={{
        padding: '0.85rem 1.25rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        background: 'var(--sk-box-info-bg)',
        border: '1px solid var(--sk-box-info-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'var(--sk-accent)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '0.85rem', flexShrink: 0,
          }}>
            {requiredDone}/{requiredCount}
          </div>
          <div style={{ minWidth: 0 }}>
            <strong style={{ fontSize: '0.9rem', display: 'block', color: 'var(--sk-ink)' }}>
              Configurazione guidata
            </strong>
            <span style={{ fontSize: '0.75rem', color: 'var(--sk-mid)' }}>
              {progressPct}% completato
            </span>
          </div>
          {/* Progress bar */}
          <div style={{
            flex: '0 1 200px', height: '6px', borderRadius: '3px',
            background: '#dbeafe', overflow: 'hidden', marginLeft: '0.5rem',
          }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: 'var(--sk-accent, #0891b2)', borderRadius: '3px',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button className="sk-btn" onClick={() => setShowWizard(true)}>
            Riprendi
          </button>
          <button
            className="sk-btn sk-btn--quiet"
            onClick={async () => {
              await apiFetch('/api/v1/tenant/onboarding/dismiss', { method: 'POST' });
              setState({ ...state, dismissed: true });
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Wizard modal — wizard dati per tutti i verticali */}
      {showWizard && (
        <SetupWizard
          state={state}
          onClose={() => setShowWizard(false)}
          onUpdate={(s) => setState(s)}
        />
      )}
    </>
  );
}

export default OnboardingBanner;
